// #5549 — the metric registry's TYPE CONTRACTS, extracted from the 2,939-line
// registry module so the definitions can be read without the seed data around
// them (ENGINEERING STANDARD (a), shrink-on-touch). Types only: no seed, no
// helper, no value — so nothing here can import the registry back.
import type { GovernedMeasureIdentity } from './governed-measure-identity';

export type MetricRegistryLayer = 'core' | 'industry' | 'company';
export type MetricTemporalType = 'flow' | 'stock' | 'ratio' | 'count';
export type MetricDirection = 'higher_is_better' | 'lower_is_better' | 'context';
export type MetricRegistrySourceOfTruthClass =
  | 'public-filing'
  | 'connector-derived'
  | 'management-estimate'
  | 'derived'
  | 'unclassified';

export type MetricRegistrySignConvention = Readonly<{
  kind: MetricDirection;
  costLine: boolean;
}>;

export type MetricRegistryComposition = Readonly<{
  formulaId: string;
  formulaVersion: string;
  inputMetricKeys: readonly string[];
}>;

export type MetricRegistryCompositionGap = Readonly<{
  metricKey: string;
  sourceFormulaRefs: readonly string[];
  source: string;
  reason: 'approved_formula_not_registered' | 'composition_inputs_not_registered';
  approvedFormulaRef?: Readonly<{
    formulaId: string;
    formulaVersion: string;
  }>;
}>;

// The governed "rolls up into" edge for a derived contribution metric — the typed
// analog of a module targetMetricRef (evidence-design §2.3). Extract-don't-invent.
export type MetricRegistryTargetRef = Readonly<{
  metricId: string;
  registryVersion: string;
}>;

/**
 * #5549 — the closed set of TARGET-COMPARISON relations a governed definition may
 * declare. One member today: `target_gap_of` says this metric states the DISTANCE
 * from some other governed measure to its approved target.
 */
export const METRIC_TARGET_COMPARISON_RELATIONS = ['target_gap_of'] as const;
export type MetricTargetComparisonRelation =
  (typeof METRIC_TARGET_COMPARISON_RELATIONS)[number];

/**
 * #5549 — the governed measure a target-gap metric states a DISTANCE ON.
 *
 * Deliberately NOT `targetMetricRef`, which answers a different question: the
 * governed measure a derived contribution ROLLS INTO. Overloading that field
 * would have made one declaration answer two unrelated questions and silently
 * widened contribution, bridge, dedupe and aggregation identity.
 *
 * The edge is ONE-WAY and ONE HOP, and exactly one caller consults it — the
 * manager's approved-target measure comparison. It grants COMPARABILITY, never
 * numeric equivalence: a percent gap is about operating income without becoming
 * USD, so unit, frame and formula checks stay authoritative everywhere else.
 */
export type MetricTargetComparisonAnchor = Readonly<{
  relation: MetricTargetComparisonRelation;
  metricRef: MetricRegistryTargetRef;
}>;

export type MetricRegistryNamedGap = Readonly<{
  gapId: 'company_metric_overlay_not_mapped';
  companyMappingId: 'helix_retail';
  industryProfileId: '3pl-logistics';
  reason: 'no_source_company_mapping_exists';
}>;

export type MetricRegistryAppliesTo = Readonly<{
  dimension: string;
  members: readonly string[] | 'all';
}>;

export type MetricRegistryCohortQualifier = Readonly<{
  dimension: string;
  qualificationFormulaId: string;
}>;
export type MetricCohortQualifier = MetricRegistryCohortQualifier;

export type MetricComponentRef = Readonly<{
  metricKey: string;
  temporalType: Extract<MetricTemporalType, 'flow' | 'stock' | 'count'>;
  cohortQualifier?: MetricRegistryCohortQualifier;
}>;

export type MetricRegistryComponents = Readonly<{
  numerator: readonly MetricComponentRef[];
  denominator: readonly MetricComponentRef[];
}>;
export type MetricComponents = MetricRegistryComponents;

export type MetricRegistryBlendPolicy =
  | Readonly<{ kind: 'additive' }>
  | Readonly<{ kind: 'never_blend' }>
  | Readonly<{ kind: 'translate_via'; translationFormulaId: string }>;
export type MetricBlendPolicy = MetricRegistryBlendPolicy;

export type MetricRegistryAggregationRule = Readonly<{
  dimension: string;
  rollup: string;
  ruleFormulaId: string;
  materialization: 'governed_derived_fact' | 'computed_on_demand';
}>;
export type MetricAggregationRule = MetricRegistryAggregationRule;
export type MetricRollupMaterialization = MetricRegistryAggregationRule['materialization'];
export type MetricRollupGrain = Readonly<{ dimension: string; rollup: string }>;
export type MetricRegistryFactScope = 'gl_dataset_run';
export type MetricQuerySnapshotScope = Readonly<{ datasetId: string; runId: string }>;

export type MetricRegistryEntry = Readonly<{
  tenantId?: string;
  metricKey: string;
  registryLayer: MetricRegistryLayer;
  industryProfileId?: string;
  companyMappingId?: string;
  registryVersion?: string;
  aliases?: readonly string[];
  appliesTo: readonly MetricRegistryAppliesTo[];
  label: string;
  definition: string;
  unit: string;
  frame: string;
  direction: MetricDirection;
  blendPolicy: MetricRegistryBlendPolicy;
  aggregationRules: readonly MetricRegistryAggregationRule[];
  reconciliationRule: string;
  requiredEvidenceClasses: readonly string[];
  benchmarkable: boolean;
  components?: MetricRegistryComponents;
  temporalType: MetricTemporalType;
  measureIdentity?: GovernedMeasureIdentity;
  cohortQualifier?: MetricRegistryCohortQualifier;
  grainSupported?: readonly string[];
  dimensionsSupported?: readonly string[];
  signConvention?: MetricRegistrySignConvention;
  sourceOfTruthClass?: MetricRegistrySourceOfTruthClass;
  /** Omitted for legacy facts; GL facts require an explicit immutable dataset run. */
  factScope?: MetricRegistryFactScope;
  composedFrom?: MetricRegistryComposition;
  targetMetricRef?: MetricRegistryTargetRef;
  /** #5549 — the base measure this target-gap metric states a distance on. */
  targetComparisonAnchor?: MetricTargetComparisonAnchor;
  ownerModuleId?: string;
  neverSumWith?: readonly string[];
  sourceBindingKeys?: readonly string[];
  source: string;
}>;

export type DimensionCatalogNeverBlendScope = Readonly<{
  appliesTo: readonly MetricRegistryAppliesTo[];
}>;

export type DimensionCatalogRollup = Readonly<{
  rollup: string;
  label: string;
  parentRollup?: string;
}>;

export type DimensionCatalogEntry = Readonly<{
  tenantId?: string;
  registryLayer?: MetricRegistryLayer;
  industryProfileId?: string;
  companyMappingId?: string;
  registryVersion?: string;
  dimension: string;
  label: string;
  physicalColumn: string;
  rollups: readonly DimensionCatalogRollup[];
  neverBlend?: DimensionCatalogNeverBlendScope;
  source: string;
}>;
export type GovernedDimensionCatalogEntry = DimensionCatalogEntry;

export type DimensionMemberSeed = Readonly<{
  dimension: string;
  memberKey: string;
  label: string;
  rollup: string;
  parentKey?: string;
  sourceLineKeys: readonly string[];
  attributes?: Readonly<Record<string, string>>;
}>;
export type GovernedDimensionMemberSeed = DimensionMemberSeed;

export type DimensionSeed = Readonly<{
  dimension: string;
  label: string;
  source: string;
  members: readonly DimensionMemberSeed[];
}>;
export type GovernedDimensionSeed = DimensionSeed;

export type MetricQuerySpec = Readonly<{
  metrics: readonly string[];
  groupBy: readonly string[];
  filters: readonly { dimension: string; memberKeys: readonly string[] }[];
  period: { type: string; from: string; to: string };
  compare?: { kind: 'vs_prior_period' | 'vs_plan' | 'vs_benchmark'; ref?: string };
  asOfPolicy: 'latest_approved' | { asOf: string };
  grain: { level: readonly { dimension: string; rollup: string }[] };
  budget?: { maxCells: number };
  snapshot?: MetricQuerySnapshotScope;
}>;

export type MetricRegistryRefusalReason =
  | 'metric_not_found'
  | 'blend_forbidden'
  | 'dimension_member_invalid'
  | 'rollup_grain_not_declared'
  | 'grain_budget_exceeded';

export type MetricRegistryAccepted = Readonly<{ ok: true }>;
export type MetricRegistryRefused = Readonly<{
  ok: false;
  reason: MetricRegistryRefusalReason;
  message: string;
  details?: Readonly<Record<string, unknown>>;
  counterProposal?: Partial<MetricQuerySpec>;
}>;
export type MetricRegistryRefusal = MetricRegistryRefused;
export type MetricRegistryValidationResult = MetricRegistryAccepted | MetricRegistryRefused;
export type MetricRegistryResult = MetricRegistryValidationResult;

export type GovernedMetricFactDedupInput = Readonly<{
  tenantId: string;
  metricKey: string;
  businessLineKey?: string | null;
  geoKey?: string | null;
  customerKey?: string | null;
  laneKey?: string | null;
  siteKey?: string | null;
  periodType: string;
  periodKey: string;
  asOf: string;
}>;
export type GovernedMetricFactKeyInput = GovernedMetricFactDedupInput;
export type GovernedMetricFactDedupResult = Readonly<{ inserted: boolean; key: string }>;

export type MetricBlendRequest = Readonly<{
  metricKeys: readonly string[];
  dimension: string;
  memberKeys: readonly string[];
}>;
